/**
 * mediaService — IndexedDB storage for question media (images & audio).
 *
 * Motivation: localStorage has a ~5 MB limit. Storing base64-encoded images
 * and audio inside question objects quickly exceeds this limit. This service
 * persists raw Blobs in IndexedDB (virtually unlimited) and returns only
 * lightweight mediaIds to be stored in the question record.
 */

const DB_NAME = "tef_media_db";
const DB_VERSION = 1;
const STORE_NAME = "media";

export interface MediaRecord {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  blob: Blob;
  createdAt: string;
}

// ─── Internal DB helpers ──────────────────────────────────────────────────────

let _db: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = (event) => {
      _db = (event.target as IDBOpenDBRequest).result;
      resolve(_db);
    };

    request.onerror = (event) => {
      console.error("mediaService: IndexedDB open error", (event.target as IDBOpenDBRequest).error);
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
}

function generateMediaId(): string {
  return `media_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const mediaService = {
  /**
   * Saves a File/Blob to IndexedDB and returns its unique mediaId.
   * Throws a descriptive error if IndexedDB is unavailable or storage fails.
   */
  async saveMedia(file: File): Promise<string> {
    try {
      const db = await openDB();
      const id = generateMediaId();

      const record: MediaRecord = {
        id,
        name: file.name,
        mimeType: file.type,
        size: file.size,
        blob: file,
        createdAt: new Date().toISOString(),
      };

      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.put(record);

        req.onsuccess = () => resolve(id);
        req.onerror = () => {
          console.error("mediaService: put error", req.error);
          reject(new Error("Não foi possível salvar a mídia. Tente reduzir o tamanho do arquivo."));
        };
      });
    } catch (err) {
      console.error("mediaService.saveMedia error:", err);
      throw new Error("Não foi possível salvar a mídia. Tente reduzir o tamanho do arquivo.");
    }
  },

  /**
   * Retrieves a Blob from IndexedDB by mediaId and returns a temporary
   * object URL for display. Returns null if the media is not found.
   *
   * IMPORTANT: The caller is responsible for revoking the URL with
   * URL.revokeObjectURL() when the component unmounts to avoid memory leaks.
   */
  async getMediaUrl(mediaId: string): Promise<string | null> {
    if (!mediaId) return null;
    try {
      const db = await openDB();

      return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(mediaId);

        req.onsuccess = () => {
          const record: MediaRecord | undefined = req.result;
          if (!record) {
            resolve(null);
            return;
          }
          const url = URL.createObjectURL(record.blob);
          resolve(url);
        };

        req.onerror = () => {
          console.error("mediaService: get error", req.error);
          resolve(null);
        };
      });
    } catch (err) {
      console.error("mediaService.getMediaUrl error:", err);
      return null;
    }
  },

  /**
   * Returns the raw Blob for a given mediaId. Useful for re-saving or
   * converting. Returns null if not found.
   */
  async getMediaBlob(mediaId: string): Promise<Blob | null> {
    if (!mediaId) return null;
    try {
      const db = await openDB();

      return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(mediaId);

        req.onsuccess = () => {
          const record: MediaRecord | undefined = req.result;
          resolve(record ? record.blob : null);
        };
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  },

  /**
   * Deletes a single media entry from IndexedDB. Silently ignores missing IDs.
   */
  async deleteMedia(mediaId: string): Promise<void> {
    if (!mediaId) return;
    try {
      const db = await openDB();

      return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.delete(mediaId);
        req.onsuccess = () => resolve();
        req.onerror = () => {
          console.warn("mediaService: delete error for", mediaId, req.error);
          resolve(); // non-fatal
        };
      });
    } catch (err) {
      console.warn("mediaService.deleteMedia error:", err);
    }
  },

  /**
   * Deletes multiple media entries in a single transaction. Non-fatal.
   */
  async deleteMediaBatch(mediaIds: string[]): Promise<void> {
    const ids = mediaIds.filter(Boolean);
    if (ids.length === 0) return;

    try {
      const db = await openDB();

      return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);

        let remaining = ids.length;
        const done = () => {
          remaining--;
          if (remaining === 0) resolve();
        };

        ids.forEach((id) => {
          const req = store.delete(id);
          req.onsuccess = done;
          req.onerror = done; // non-fatal
        });
      });
    } catch (err) {
      console.warn("mediaService.deleteMediaBatch error:", err);
    }
  },

  /**
   * Collects all mediaIds stored on a question object (options, audio, image).
   * Useful before deletion.
   */
  collectMediaIds(question: any): string[] {
    const ids: string[] = [];

    if (question?.audioMediaId) ids.push(question.audioMediaId);
    if (question?.imageMediaId) ids.push(question.imageMediaId);

    if (Array.isArray(question?.options)) {
      question.options.forEach((opt: any) => {
        if (opt?.mediaId) ids.push(opt.mediaId);
      });
    }

    return ids.filter(Boolean);
  },
};
